<template>
  <div class="chatbot-wrapper" v-if="isActive" :class="{ 'chat-open': isOpen }">
    <!-- Chat Icon / Launcher -->
    <v-btn
      v-if="!isOpen"
      color="primary"
      fab
      dark
      large
      bottom
      right
      fixed
      elevation="8"
      @click="isOpen = true"
      class="chat-launcher"
    >
      <v-icon>{{ store.NOTARIA?.Personalizar?.ChatbotIcon || 'mdi-message-text' }}</v-icon>
    </v-btn>

    <!-- Chat Window -->
    <v-card v-if="isOpen" class="chat-window d-flex flex-column elevation-10" :class="{ 'expanded': isExpanded }" width="350" height="500">
      <v-toolbar color="primary" dark dense flat>
        <v-icon class="mr-2 ml-3">{{ store.NOTARIA?.Personalizar?.ChatbotIcon || 'mdi-message-text' }}</v-icon>
        <span class="subtitle-1 font-weight-bold">{{ store.NOTARIA?.Personalizar?.ChatbotNombre || 'Asistente' }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="isExpanded = !isExpanded" small>
          <v-icon>{{ isExpanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
        </v-btn>
        <v-btn icon @click="isOpen = false" small title="Cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="flex-grow-1 overflow-y-auto chat-messages pa-3" ref="messagesContainer">
        <div v-for="(msg, index) in messages" :key="index" :class="['message-bubble', msg.role]">
          <div class="message-content" v-html="formatResponse(msg.content)"></div>
        </div>
        <div v-if="loading" class="message-bubble bot typing-indicator">
          <span></span><span></span><span></span>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      
      <v-card-actions class="pa-2">
        <v-text-field
          v-model="userInput"
          dense
          outlined
          hide-details
          :placeholder="chatFinished ? 'Conversación finalizada' : $t('placeholderChatbot')"
          :disabled="chatFinished || loading"
          @keyup.enter="sendMessage"
        >
          <template v-slot:append-inner>
            <v-icon @click="sendMessage" :disabled="!userInput.trim() || loading || chatFinished" color="primary">mdi-send</v-icon>
          </template>
        </v-text-field>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch } from 'vue';
import { useStore } from '@/store';
import { useUtils, useNuxtApp } from '#imports';
import { VueReCaptcha, useReCaptcha } from 'vue-recaptcha-v3';

const store = useStore();
const { $t } = useUtils();
const { vueApp } = useNuxtApp();
const overQuota = ref(false);
const isConfigured = ref(true);
const isOpen = ref(false);
const isExpanded = ref(false);
const chatFinished = ref(false);
const formSubmitted = ref(false);
const loading = ref(false);
const userInput = ref('');
const messagesContainer = ref(null);
const dumbModeState = ref(0);
const dumbModeData = ref({ name: '', email: '', message: '' });

const isActive = computed(() => {
  const baseActive = store.NOTARIA?.Personalizar?.ChatbotActivo === true && isConfigured.value;
  if (!baseActive) return false;
  if (overQuota.value) {
    return store.NOTARIA?.Personalizar?.ChatbotModoRecogidaSinSaldo === true;
  }
  return true;
});

const initialMessage = computed(() => {
  if (overQuota.value && store.NOTARIA?.Personalizar?.ChatbotModoRecogidaSinSaldo) {
    return $t('chatbotDumbInitialMessage');
  }
  return store.NOTARIA?.Personalizar?.ChatbotMensajeBienvenida || $t('chatbotGreeting');
});

const messages = ref([]);

onMounted(async () => {
  try {
    if (store.recaptchaSiteKey) {
      vueApp.use(VueReCaptcha, {
        siteKey: store.recaptchaSiteKey,
        loaderOptions: {
          autoHideBadge: false,
        },
      });
    }
  } catch (e) { }

  try {
    const idNotaria = store.NOTARIA?.id || store.NOTARIA?.IdValue;
    if (idNotaria) {
      const baseUrl = process.env.NODE_ENV === 'development' ? (store.api_url || (typeof window !== 'undefined' ? window.location.origin + '/' : '/')) : 'https://controlpanel.notin.es/';
      const status = await $fetch(`${baseUrl}api/Notarias/chatbot/status/${idNotaria}`);
      if (status && status.percentage >= 100) {
        overQuota.value = true;
      }
    }
  } catch(e) { console.error("Error al comprobar cuota IA:", e); }

  initChat();
});

const initChat = () => {
  // El backend ya desactiva ChatbotActivo si se supera la cuota al descargar la notaría
  if (store.NOTARIA?.Personalizar?.ChatbotActivo) {
    messages.value = [];
    // Cargamos el mensaje inicial
    messages.value.push({
      role: 'bot',
      content: initialMessage.value
    });
    
    // Detección robusta de dispositivo
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 1264;
    const isMobile = isMobileUA || isSmallScreen;

    const p = store.NOTARIA?.Personalizar;
    
    // Nueva lógica: Los parámetros nuevos mandan. Solo usamos el antiguo si los nuevos no existen (son undefined o null).
    let shouldAutoOpen = false;
    
    if (isMobile) {
      // En móvil solo abrimos si el parámetro específico de móvil es true
      shouldAutoOpen = (p?.ChatbotAutoOpenMobile === true);
      // Por defecto en móviles NO lo abrimos si el parámetro no existe
      if (p?.ChatbotAutoOpenMobile == null) {
        shouldAutoOpen = false;
      }
    } else {
      // En escritorio igual
      shouldAutoOpen = (p?.ChatbotAutoOpenDesktop === true);
      if (p?.ChatbotAutoOpenDesktop == null) {
        shouldAutoOpen = p?.ChatbotAutoOpen === true;
      }
    }

    // Debug para el usuario en consola
    console.log(`[Chatbot IA] Modo Móvil: ${isMobile} | AutoOpenMobile: ${p?.ChatbotAutoOpenMobile} | AutoOpenDesktop: ${p?.ChatbotAutoOpenDesktop} | Resultado: ${shouldAutoOpen}`);

    if (shouldAutoOpen && !isOpen.value) {
      setTimeout(() => {
        isOpen.value = true;
      }, 1000);
    }
  }
};

// Reiniciar si cambia el idioma y no hay conversación activa real
watch(() => store.idioma, () => {
  if (messages.value.length <= 1) {
    initChat();
  }
});

const formatResponse = (text) => {
  if (!text) return '';
  
  // 1. Convertir negritas y cursivas (Markdown básico)
  let html = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                 .replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // 2. Enlaces Markdown: [texto](url)
  html = html.replace(/\[(.*?)\]\((https?:\/\/.*?)\)/g, '<a href="$2" target="_blank" class="chat-link">$1</a>');
  
  // 3. Enlaces automáticos (URLs planas)
  // Buscamos URLs que empiecen por http y que NO estén ya dentro de un atributo href o una etiqueta
  const urlRegex = /(^|\s)(https?:\/\/[^\s<]+)/g;
  html = html.replace(urlRegex, (match, p1, p2) => {
      // Evitar doble procesado si ya es parte de un enlace markdown
      if (p1.includes('href=') || p1.includes('">')) return match;
      return p1 + `<a href="${p2}" target="_blank" class="chat-link">${p2}</a>`;
  });

  // 4. Saltos de línea
  return html.replace(/\n/g, '<br/>');
};

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value?.$el) {
    messagesContainer.value.$el.scrollTop = messagesContainer.value.$el.scrollHeight;
  } else if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const handleDumbMode = async (text) => {
  try {
    if (dumbModeState.value === 0) {
      dumbModeData.value.name = text;
      dumbModeState.value = 1;
      messages.value.push({ role: 'bot', content: $t('chatbotDumbEmailRequest') });
    } else if (dumbModeState.value === 1) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[0-9+\-\s()]{7,20}$/;
      if (!emailRegex.test(text) && !phoneRegex.test(text)) {
        messages.value.push({ role: 'bot', content: $t('chatbotDumbEmailError') });
        return;
      }
      dumbModeData.value.email = text;
      dumbModeState.value = 2;
      messages.value.push({ role: 'bot', content: $t('chatbotDumbMessageRequest') });
    } else if (dumbModeState.value === 2) {
      dumbModeData.value.message = text;
      dumbModeState.value = 3;
      
      let recaptchaToken = '';
      try {
        const recaptcha = useReCaptcha();
        if (recaptcha && recaptcha.recaptchaLoaded) {
          await recaptcha.recaptchaLoaded();
          recaptchaToken = await recaptcha.executeRecaptcha('email');
        }
      } catch(err) {
        console.warn("Recaptcha bypass/error:", err);
      }

      // Log survival conversation in backend for stats and get updated count
      let survivalCount = null;
      try {
        const idNotaria = store.NOTARIA?.id || store.NOTARIA?.IdValue;
        if (idNotaria) {
          const baseUrl = process.env.NODE_ENV === 'development' 
            ? (store.api_url || (typeof window !== 'undefined' ? window.location.origin + '/' : '/'))
            : 'https://controlpanel.notin.es/';
          
          // Usamos el controlador de Chatbot que es el principal para estos temas
          const logRes = await $fetch(`${baseUrl}api/Chatbot/log-survival/${idNotaria}`, { method: "POST" });
          if (logRes && logRes.countSurvival) {
            survivalCount = logRes.countSurvival;
          }
        }
      } catch(logErr) {
        console.warn("Error logging survival conversation (stats will not be updated):", logErr);
      }

      const originUrl = typeof window !== 'undefined' ? window.location.origin : 'Web Notaría';
      let cuerpo = `Mensaje enviado desde el Asistente en Modo Recogida: ${originUrl}<br><br>
Remitente: ${dumbModeData.value.name}<br>
Contacto: ${dumbModeData.value.email}<br>
Mensaje: ${dumbModeData.value.message}<br><br><br>
<b>Generado automáticamente por el asistente en modo Recogida sin saldo.</b>`;

      if (survivalCount) {
        cuerpo += `<br><br><i>Nota: Este es el cliente nº <b>${survivalCount}</b> que contacta este mes a través del modo supervivencia por falta de tokens.</i>`;
        cuerpo += `<br><br>¿Quieres que la IA siga respondiendo y gestionando automáticamente estos clientes? Puedes ampliar el plan accediendo a <a href="https://controlpanel.notin.es/chatbot-ia">https://controlpanel.notin.es/chatbot-ia</a> o enviando un correo a <a href="mailto:web@unidata.es">web@unidata.es</a>`;
      }

      const mailSubject = `Contacto WEB - ${store.NOTARIA?.Nombre} - ${dumbModeData.value.name} - Consulta (Modo Recogida) - IA`;

      const model = {
        perfil: "info_recaptcha_web_notaria",
        cuerpo: cuerpo,
        is_cuerpo_html: true,
        asunto: mailSubject,
        destinatarios: [{ nombre: store.NOTARIA?.Nombre, direccion: store.NOTARIA?.Contacto?.Email }],
        destinatarios_cco: [{ nombre: "webNotin", direccion: "web@notin.net" }],
        recaptcha: {
          id_credential: store.NOTARIA?.Personalizar?.RecaptchaGroup || 1,
          token: recaptchaToken,
          action: 'email'
        },
        data: {
          id_notaria: store.NOTARIA?.id || store.NOTARIA?.IdValue,
          Fecha: new Date().getTime().toString(),
          Name: dumbModeData.value.name,
          Email: dumbModeData.value.email,
          Phone: "",
          Subject: mailSubject,
          Message: dumbModeData.value.message
        }
      };

      const mailService = 'https://mailing.notin.es/api/email';
      await $fetch(mailService, { method: "POST", body: model, headers: { 'Content-Type': 'application/json' } });
      
      messages.value.push({ role: 'bot', content: $t('chatbotDumbSuccessMessage') });
      chatFinished.value = true;
    }
  } catch (e) {
    messages.value.push({ role: 'bot', content: $t('chatbotDumbErrorMessage') });
  } finally {
    loading.value = false;
    await scrollToBottom();
  }
};

const sendMessage = async () => {
  const text = userInput.value.trim();
  if (!text || loading.value) return;

  userInput.value = '';
  
  // Add user message
  messages.value.push({
    role: 'user',
    content: text
  });
  
  loading.value = true;
  await scrollToBottom();

  if (overQuota.value) {
    await handleDumbMode(text);
    return;
  }

  try {
    const idNotaria = store.NOTARIA?.id || store.NOTARIA?.IdValue;
    if (!idNotaria) return;
    const baseUrl = process.env.NODE_ENV === 'development' 
      ? (store.api_url || (typeof window !== 'undefined' ? window.location.origin + '/' : '/'))
      : 'https://controlpanel.notin.es/';
    const url = `${baseUrl}api/Chatbot/ask/${idNotaria}`;

    // Mapeamos el historial excluyendo el mensaje de bienvenida y el actual
    // Roles para Gemini: 'user', 'model'
    const history = messages.value.slice(1, -1).map(m => ({
      Role: m.role === 'bot' ? 'model' : 'user',
      Content: m.content
    }));

    const response = await $fetch(url, {
      method: 'POST',
      body: { 
        Message: text,
        History: history,
        Language: store.idioma
      }
    });

    let botText = response.response || 'No he podido entender tu consulta correctamente.';
    
    // Interceptar comando de envío de formulario de contacto
    const match = botText.match(/\[CONTACT_FORM_SUBMIT:(.*?)\]/s);
    if (match && match[1]) {
      if (formSubmitted.value) {
        botText = botText.replace(/\[CONTACT_FORM_SUBMIT:.*?\]/s, "");
      } else {
        try {
          let formData = {};
          try {
            let rawJson = match[1].trim();
            // Limpiar posibles bloques de código markdown que la IA suele añadir
            if (rawJson.startsWith('```json')) rawJson = rawJson.substring(7);
            else if (rawJson.startsWith('```')) rawJson = rawJson.substring(3);
            if (rawJson.endsWith('```')) rawJson = rawJson.substring(0, rawJson.length - 3);
            
            rawJson = rawJson.trim();
            
            // Reemplazar saltos de línea reales dentro de los valores por un espacio o escaparlos, 
            // pero normalmente JSON.parse falla si hay saltos de línea sin escapar en un string.
            // La IA (Gemini/Claude) suele enviar JSON válido y escapado.
            formData = JSON.parse(rawJson);
        } catch(err) {
          console.error("JSON Parse error:", err, match[1]);
          throw new Error("Invalid JSON format from AI");
        }
        
        // Cargar recaptcha y obtener token
        let recaptchaToken = '';
        try {
          const recaptcha = useReCaptcha();
          if (recaptcha && recaptcha.recaptchaLoaded) {
            await recaptcha.recaptchaLoaded();
            recaptchaToken = await recaptcha.executeRecaptcha('email');
          }
        } catch(err) {
          console.warn("Recaptcha bypass/error:", err);
        }

        const originUrl = typeof window !== 'undefined' ? window.location.origin : 'Web Notaría';
        const cuerpo = `Mensaje enviado desde el Asistente Virtual IA: ${originUrl}<br><br>
Remitente: ${formData.name}<br>
Correo: <a href="mailto:${formData.email}">${formData.email}</a><br>
Teléfono: ${formData.phone}<br>
Asunto: ${formData.subject}<br>
Mensaje: ${formData.message}<br><br><br>
<b>Generado automáticamente por IA. Por favor, responda haciendo clic en la dirección de correo proporcionada arriba.</b>`;

        const ccoList = [{ nombre: "webNotin", direccion: "web@notin.net" }];
          if (formData.teamEmail && store.NOTARIA?.Personalizar?.ChatbotCopiarEquipo) {
            ccoList.push({ nombre: "Equipo", direccion: formData.teamEmail });
          }

          const finalSubject = formData.subject || "Consulta General";
          const mailSubject = `Contacto WEB - ${store.NOTARIA?.Nombre} - ${formData.name} - ${finalSubject} - IA`;

          const model = {
            perfil: "info_recaptcha_web_notaria",
            cuerpo: cuerpo,
            is_cuerpo_html: true,
            asunto: mailSubject,
            destinatarios: [{ nombre: store.NOTARIA?.Nombre, direccion: store.NOTARIA?.Contacto?.Email }],
            destinatarios_cco: ccoList,
            recaptcha: {
              id_credential: store.NOTARIA?.Personalizar?.RecaptchaGroup || 1,
              token: recaptchaToken,
              action: 'email'
            },
            data: {
              id_notaria: store.NOTARIA?.id || store.NOTARIA?.IdValue,
              Fecha: new Date().getTime().toString(),
              Name: formData.name,
              Email: formData.email,
              Phone: formData.phone,
              Subject: mailSubject,
              Message: formData.message
            }
          };

          const mailService = 'https://mailing.notin.es/api/email';
          await $fetch(mailService, { method: "POST", body: model, headers: { 'Content-Type': 'application/json' } });
          
          formSubmitted.value = true;
          // Eliminamos el tag sin añadir texto extra, ya que la IA ya redacta su propia confirmación
          botText = botText.replace(/\[CONTACT_FORM_SUBMIT:.*?\]/s, "");
      } catch (e) {
        console.error("Error al enviar formulario desde IA:", e);
        botText = botText.replace(/\[CONTACT_FORM_SUBMIT:.*?\]/s, "Lo siento, ha ocurrido un error al intentar enviar el mensaje a la notaría. Por favor, inténtalo más tarde o contáctanos directamente por teléfono.");
      }
    }
    }

    messages.value.push({
      role: 'bot',
      content: botText
    });

    if (response.isFinished) {
      chatFinished.value = true;
    }
  } catch (error) {
    console.error("Chatbot error:", error);
    
    let errorMsg = 'Lo siento, el sistema no se encuentra disponible ahora mismo.';
    if (error.response?.status === 403) {
      errorMsg = 'Lo siento, por el momento no podemos atender más consultas. Por favor, contacte con nosotros directamente por teléfono o email.';
    }

    messages.value.push({
      role: 'bot',
      content: errorMsg
    });
  } finally {
    loading.value = false;
    await scrollToBottom();
  }
};
</script>

<style>
/* Estilos globales para enlaces dentro del chatbot (v-html) */
.chat-window .chat-link {
  color: #1a73e8 !important; 
  text-decoration: underline !important;
  font-weight: 600 !important;
}

.chat-window .message-bubble.user .chat-link {
  color: #fff !important;
}
</style>

<style scoped>
.chatbot-wrapper {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 99999; /* Aumentado para asegurar que queda por encima del menú superior */
}

.chat-launcher {
  bottom: 85px;
  right: 20px;
  position: fixed !important;
  border-radius: 50% !important;
  width: 64px !important;
  height: 64px !important;
  min-width: 0 !important;
}

.chat-window {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
  position: fixed;
  bottom: 85px;
  right: 20px;
  z-index: 99999;
  transition: all 0.3s ease;
}

.chat-window.expanded {
  width: 600px !important;
  height: 80vh !important;
  max-width: 90vw;
}

.chat-messages {
  background-color: #f7f9fa;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 0.95rem;
  line-height: 1.4;
  word-wrap: break-word;
}

.message-bubble.user {
  align-self: flex-end;
  background-color: #1976D2; /* Color fallback seguro */
  background-color: rgb(var(--v-theme-primary)); 
  color: white;
  border-bottom-right-radius: 4px;
}

.message-bubble.bot {
  align-self: flex-start;
  background-color: white;
  color: #333;
  border: 1px solid #e0e0e0;
  border-bottom-left-radius: 4px;
}

/* Typing indicator */
.typing-indicator span {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #90a4ae;
  border-radius: 50%;
  margin-right: 4px;
  animation: typing 1.4s infinite ease-in-out both;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}
</style>
